/// WelcomeTab: Welcome screen shown when no editor tabs are open.
/// Provides quick action buttons for creating new diagrams.

import { useCallback } from "react";
import { GitBranch, FileCode, FolderOpen } from "lucide-react";
import { useEditorStore } from "@/stores/editorStore";
import type { EditorType } from "@/types/editor";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let tabCounter = 0;

function useCreateTab() {
  const addTab = useEditorStore((s) => s.addTab);

  return useCallback(
    (editorType: EditorType) => {
      tabCounter += 1;
      const label = editorType === "bpmn" ? "BPMN" : "Node";
      addTab({
        id: `tab-${Date.now()}-${tabCounter}`,
        title: `Untitled ${label} ${tabCounter}`,
        editorType,
        content: "",
        dirty: false,
        canUndo: false,
        canRedo: false,
      });
    },
    [addTab],
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WelcomeTab() {
  const createTab = useCreateTab();

  return (
    <div data-testid="welcome-tab" className="flex flex-1 flex-col items-center justify-center gap-8 bg-zinc-900 select-none">
      {/* Title */}
      <div className="flex flex-col items-center gap-2">
        <h1 className="text-3xl font-bold text-zinc-100 tracking-tight">InovaBPM</h1>
        <p className="text-sm text-zinc-500">Visual Workflow Editor</p>
      </div>

      {/* Quick actions */}
      <div className="flex gap-4">
        <QuickAction
          icon={<GitBranch className="h-8 w-8 text-blue-400" />}
          label="New BPMN Diagram"
          description="Create a business process diagram"
          onClick={() => createTab("bpmn")}
        />
        <QuickAction
          icon={<FileCode className="h-8 w-8 text-green-400" />}
          label="New Node Flow"
          description="Create a node-based automation flow"
          onClick={() => createTab("nodeflow")}
        />
        <QuickAction
          icon={<FolderOpen className="h-8 w-8 text-amber-400" />}
          label="Open File"
          description="Open an existing diagram"
          onClick={() => {
            // File open dialog will be wired later
          }}
        />
      </div>

      {/* Keyboard shortcuts hint */}
      <div className="text-xs text-zinc-600 flex gap-4">
        <span>Ctrl+N New BPMN</span>
        <span>Ctrl+Shift+N New Node</span>
        <span>Ctrl+O Open</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// QuickAction card
// ---------------------------------------------------------------------------

interface QuickActionProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
}

function QuickAction({ icon, label, description, onClick }: QuickActionProps) {
  return (
    <button
      type="button"
      className="flex flex-col items-center gap-3 rounded-lg border border-zinc-700 bg-zinc-800/50 px-6 py-5 hover:border-zinc-600 hover:bg-zinc-800 transition-colors w-48"
      onClick={onClick}
    >
      {icon}
      <span className="text-sm font-medium text-zinc-200">{label}</span>
      <span className="text-xs text-zinc-500 text-center">{description}</span>
    </button>
  );
}
